/**
 * Step 3: Filter Configuration (Optional)
 *
 * Filter features by property values using:
 * - Exact: Select specific values
 * - Pattern: Regex or wildcard matching
 * - Comparison: Numeric comparisons (>, <, etc.)
 * - Ranking: Top/bottom N features
 */

import { useState, useMemo } from "react";
import { Plus, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useStudioStore,
  selectDataset,
  selectFilters,
  addFilter,
  updateFilter,
  removeFilter,
} from "@/stores/useStudioStore";
import { FilterRuleEditor } from "@/components/studio/FilterRuleEditor";

export function Step3FilterConfiguration() {
  const dataset = useStudioStore(selectDataset);
  const filters = useStudioStore(selectFilters);
  const [selectedProperty, setSelectedProperty] = useState<string>("");

  // Get available properties for filtering
  const availableProperties = useMemo(() => {
    if (!dataset.collection) return [];
    const propertySet = new Set<string>();
    dataset.collection.features.forEach((feature) => {
      if (feature.properties) {
        Object.keys(feature.properties).forEach((key) => propertySet.add(key));
      }
    });
    return Array.from(propertySet).sort();
  }, [dataset.collection]);

  // Properties that don't have filters yet
  const unusedProperties = availableProperties.filter(
    (prop) => !filters.some((f) => f.propertyKey === prop)
  );

  // Extract unique values for each property (for exact mode)
  const propertyValues = useMemo(() => {
    if (!dataset.collection) return new Map<string, string[]>();

    const valuesMap = new Map<string, Set<string>>();

    dataset.collection.features.forEach((feature) => {
      if (feature.properties) {
        Object.entries(feature.properties).forEach(([key, value]) => {
          if (!valuesMap.has(key)) {
            valuesMap.set(key, new Set());
          }
          // Convert to string and add if not null/undefined
          if (value !== null && value !== undefined) {
            const stringValue = Array.isArray(value)
              ? value.join(", ")
              : String(value);
            valuesMap.get(key)?.add(stringValue);
          }
        });
      }
    });

    // Convert sets to sorted arrays
    const result = new Map<string, string[]>();
    valuesMap.forEach((valueSet, key) => {
      result.set(key, Array.from(valueSet).sort());
    });

    return result;
  }, [dataset.collection]);

  const handleAddFilter = () => {
    if (!selectedProperty) return;

    addFilter({
      id: `filter-${Date.now()}`,
      propertyKey: selectedProperty,
      mode: "exact",
      values: [],
    });

    setSelectedProperty("");
  };

  if (!dataset.collection) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <p>No dataset loaded. Please go back to Step 1.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Add Filters (Optional)</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Narrow down which features appear in the quiz
            </p>
          </div>
          <Badge variant="outline">Optional</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info box */}
        <div className="p-4 rounded-lg bg-muted/50 border">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">What are filters?</p>
              <p>
                Filters let you focus on specific features. For example, you could filter to only
                show regions with population &gt; 1 million, or regions whose name starts with "S".
              </p>
            </div>
          </div>
        </div>

        {/* Current state */}
        {filters.length === 0 ? (
          <div className="p-8 rounded-lg border-2 border-dashed text-center">
            <p className="text-sm text-muted-foreground">
              No filters applied - all {dataset.collection.features.length} features will be included
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Active Filters</p>
              <Badge variant="secondary">{filters.length} filter(s)</Badge>
            </div>
            {filters.map((filter) => (
              <FilterRuleEditor
                key={filter.id}
                filter={filter}
                availableValues={propertyValues.get(filter.propertyKey) || []}
                onUpdate={(updates) => updateFilter(filter.id, updates)}
                onRemove={() => removeFilter(filter.id)}
              />
            ))}
          </div>
        )}

        {/* Add filter */}
        {unusedProperties.length > 0 ? (
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium">Add Filter</p>
            <div className="flex gap-2">
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select property..." />
                </SelectTrigger>
                <SelectContent>
                  {unusedProperties.map((prop) => (
                    <SelectItem key={prop} value={prop}>
                      {prop}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddFilter}
                disabled={!selectedProperty}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
        ) : filters.length > 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">
            All properties have filters
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
